import {MaybeRef, ref, unref} from "vue";

type RuleType = "string" | "array" | "object" | "number" | "any";

type ValidatorFn<T = any> = (value: T) => boolean;

type Rule = {
  type: RuleType | ((...args: any[]) => RuleType);
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  length?: number;
  min?: number;
  max?: number;
  isInteger?: boolean;
  validator?: ValidatorFn;
};

export type Rules<T> = {
  [K in keyof T]?: Rule;
};

type CheckBaseFnArgs = {value: any; type: RuleType};
type CheckMinLengthFnArgs = CheckBaseFnArgs & {minLength: number};
type CheckMaxLengthFnArgs = CheckBaseFnArgs & {maxLength: number};
type CheckLengthFnArgs = CheckBaseFnArgs & {length: number};
type CheckMinimumValueFnArgs = CheckBaseFnArgs & {min: number};
type CheckMaximumValueFnArgs = CheckBaseFnArgs & {max: number};

const check = {
  type: ({value, type}: CheckBaseFnArgs) => {
    if ([null, undefined].includes(value)) return true;

    if (type === "any") return true;

    if (type === "array") return Array.isArray(value);

    if (type === "object")
      return typeof value === "object" && !Array.isArray(value);

    // handling html inputs that may return a string
    if (type === "number")
      return !Number.isNaN(+value) && Number.isFinite(+value);

    return typeof value === type;
  },
  required: ({value}: Pick<CheckBaseFnArgs, "value">) => {
    return !["", null, undefined].includes(value);
  },
  minLength: ({value, type, minLength}: CheckMinLengthFnArgs) => {
    if (!["array", "string"].includes(type)) return false;

    if (!value) return false;
    if (minLength == null) return false;

    return value.length >= minLength;
  },
  maxLength: ({value, type, maxLength}: CheckMaxLengthFnArgs) => {
    if (!["array", "string"].includes(type)) return false;

    if (!value) return false;
    if (maxLength == null) return false;

    return value.length <= maxLength;
  },
  length: ({value, type, length}: CheckLengthFnArgs) => {
    if (!["array", "string"].includes(type)) return false;

    if (!value) return false;
    if (length == null) return false;

    return value.length === length;
  },
  min: ({value, type, min}: CheckMinimumValueFnArgs) => {
    if (type !== "number") return false;

    return value >= min;
  },
  max: ({value, type, max}: CheckMaximumValueFnArgs) => {
    if (type !== "number") return false;

    return value <= max;
  },
  isInteger: ({value, type}: CheckBaseFnArgs) => {
    if (type !== "number") return false;

    return Number.isInteger(value as number);
  },
};

export class FormValidator<T extends Record<string, unknown>> {
  private form: T;
  private rules: Rules<T>;
  private errors: Record<keyof T, boolean>;

  constructor(form: T, rules: Rules<T>) {
    this.form = form;
    this.rules = rules;
  }

  private isValidField(fieldName: keyof T, rule: Rule): boolean {
    const {
      type: originalType,
      required,
      minLength,
      maxLength,
      length,
      min,
      max,
      isInteger,
      validator,
    } = rule;

    const value = this.form[fieldName];

    const type =
      typeof originalType === "function" ? originalType() : originalType;

    if (!check.type({value, type})) return false;

    if (required && !check.required({value})) return false;

    if (
      Object.hasOwn(rule, "minLength") &&
      !check.minLength({value, type, minLength})
    )
      return false;

    if (
      Object.hasOwn(rule, "maxLength") &&
      !check.maxLength({value, type, maxLength})
    )
      return false;

    if (Object.hasOwn(rule, "length") && !check.length({value, type, length}))
      return false;

    if (Object.hasOwn(rule, "min") && !check.min({value, type, min}))
      return false;

    if (Object.hasOwn(rule, "max") && !check.max({value, type, max}))
      return false;

    if (isInteger && !check.isInteger({value, type})) return false;

    if (validator && !validator(value)) return false;

    return true;
  }

  validateForm(): {isValid: boolean; errors: Record<keyof T, boolean>} {
    this.errors = {} as Record<keyof T, boolean>;

    for (const key in this.rules) {
      const isValidField = this.isValidField(key, this.rules[key]);
      if (!isValidField) this.errors[key] = true;
    }

    return {
      isValid: !Object.keys(this.errors).length,
      errors: this.errors,
    };
  }
}

export function useValidateForms<
  T extends Record<string, unknown>,
  R extends T[] = T[],
>(
  forms: {[K in keyof R]: MaybeRef<R[K]>},
  rules: {[K in keyof R]: Rules<R[K]>},
) {
  const errors = ref({} as Record<string, boolean>);

  function validateForms(): boolean {
    let isValidForm = true;

    errors.value = {};

    for (const [index, form] of Object.entries(forms)) {
      const unreffedForm = Object.fromEntries(
        Object.entries(unref(form)).map(([k, v]) => [k, unref(v)]),
      );

      const formValidator = new FormValidator(
        unreffedForm,
        rules[+index] || ({} as Rules<T>),
      );

      const result = formValidator.validateForm();

      if (!result.isValid) isValidForm = false;

      Object.assign(errors.value, {...result.errors});
    }

    return isValidForm;
  }

  return {errors, validateForms};
}
